import React from 'react';
import { Space } from 'antd';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { typeStyle } from 'constants/typography';

export type InputAddonProps = {
  addonBefore?: React.ReactNode;
  addonAfter?: React.ReactNode;
  /** Provider brand colour for the addon text, as the secret-reference prefixes use. */
  addonColor?: string;
};

type InputAddonGroupProps = InputAddonProps & {
  disabled?: boolean;
  invalid?: boolean;
  className?: string;
  style?: React.CSSProperties;
  children: React.ReactNode;
};

export const hasAddon = ({ addonBefore, addonAfter }: InputAddonProps) => Boolean(addonBefore || addonAfter);

/**
 * antd 5.29 deprecates Input's and InputNumber's addons for Space.Compact. The field and its addons
 * render as compact items, and the addon is drawn the way the deprecated prop drew it, so a field keeps
 * its look. The caller's class and style go on the group, where antd put them for a field with an addon.
 */
export const InputAddonGroup: React.FC<InputAddonGroupProps> = ({
  addonBefore,
  addonAfter,
  addonColor,
  disabled,
  invalid,
  className,
  style,
  children
}) => (
  <Group block className={className} style={style}>
    {addonBefore && (
      <Addon side="before" addonColor={addonColor} disabled={disabled} invalid={invalid}>
        {addonBefore}
      </Addon>
    )}
    {children}
    {addonAfter && (
      <Addon side="after" addonColor={addonColor} disabled={disabled} invalid={invalid}>
        {addonAfter}
      </Addon>
    )}
  </Group>
);

// The field fills the row and keeps its own height: a taller addon, such as a button, grows the row and
// not the field.
const Group = styled(Space.Compact)`
  > .ant-input,
  > .ant-input-number {
    align-self: flex-start;
  }

  > .ant-input-number {
    width: 100%;
  }
`;

type AddonStyleProps = {
  side: 'before' | 'after';
  addonColor?: string;
  disabled?: boolean;
  invalid?: boolean;
};

const outerRadius = ({ side }: AddonStyleProps) =>
  side === 'before'
    ? css`
        border-start-start-radius: var(--border-radius-100);
        border-end-start-radius: var(--border-radius-100);
        border-start-end-radius: 0;
        border-end-end-radius: 0;
        /* No end border to overlap, so the field starts where the addon ends. */
        margin-inline-end: 0;
      `
    : css`
        border-start-start-radius: 0;
        border-end-start-radius: 0;
        border-start-end-radius: var(--border-radius-100);
        border-end-end-radius: var(--border-radius-100);
      `;

const stateCss = ({ disabled, invalid }: AddonStyleProps) => css`
  ${
    disabled &&
    css`
      color: var(--text-disabled);
      border-color: var(--component-field-border-disabled);
      cursor: not-allowed;
    `
  }

  ${
    invalid &&
    css`
      border-color: var(--component-field-border-error);
    `
  }
`;

/**
 * Doubled class: antd declares the addon and its compact corners at one class behind `:where()`.
 *
 * antd isolated an addon's content from the group, which Space.Addon does not, so a button or select
 * inside inherits compact styling. The rules below put back what the deprecated addon drew: a select
 * fills the addon without a frame of its own, and a button keeps its corners.
 */
const Addon = styled(Space.Addon, {
  shouldForwardProp: prop => !['side', 'addonColor', 'disabled', 'invalid'].includes(prop)
})<AddonStyleProps>`
  && {
    ${typeStyle.bodyMd}
    justify-content: center;
    text-align: center;
    padding-inline: calc(var(--padding-md) - var(--border-width-xs));
    border-color: var(--border-input-field-default-2);
    background-color: var(--surface-main-cards);
    color: ${({ addonColor }) => addonColor ?? 'var(--text-heading)'};
    ${outerRadius}
    ${stateCss}
  }

  && .ant-select {
    margin-block: calc(-1 * var(--border-width-xs));
    margin-inline: calc(var(--border-width-xs) - var(--padding-md));
  }

  && .ant-select-single .ant-select-selector {
    background-color: inherit;
    border-color: transparent;
    box-shadow: none;
  }

  && .ant-btn.ant-btn {
    border-radius: var(--border-radius-100);
  }
`;
